import React, { useState, useMemo } from 'react';
import {
  Search,
  X,
  ChevronUp,
  ChevronDown,
  Eye,
  Loader2
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';
import { cn } from '@/lib/utils';
import { parseAsLocalDate } from '@/utils/dateHelpers';
import type { BankerRecon } from '@/types/banker-recon';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface FinalizedReconsTableProps {
  recons: BankerRecon[];
  loading: boolean;
  onViewRecon: (recon: BankerRecon) => void;
}

type SortDirection = 'asc' | 'desc' | null;

type SortField =
  | 'loan_number'
  | 'borrower'
  | 'lo'
  | 'loan_amount'
  | 'net_profit'
  | 'lo_payout'
  | 'investor_purchase_date'
  | 'early_payoff_date'
  | 'finalized_at';

type TimePeriod =
  | 'all'
  | 'this_month'
  | 'last_month'
  | 'this_quarter'
  | 'last_quarter'
  | 'this_year'
  | 'last_year';

interface ColumnDef {
  key: SortField;
  label: string;
  sortable: boolean;
  align?: 'left' | 'right' | 'center';
}

const COLUMNS: ColumnDef[] = [
  { key: 'loan_number', label: 'Loan #', sortable: true },
  { key: 'borrower', label: 'Borrower', sortable: true },
  { key: 'lo', label: 'LO', sortable: true },
  { key: 'loan_amount', label: 'Loan Amount', sortable: true, align: 'right' },
  { key: 'net_profit', label: 'Net Profit', sortable: true, align: 'right' },
  { key: 'lo_payout', label: 'LO Payout', sortable: true, align: 'right' },
  { key: 'investor_purchase_date', label: 'Purchased', sortable: true },
  { key: 'early_payoff_date', label: 'EPO Clears', sortable: true },
  { key: 'finalized_at', label: 'Finalized', sortable: true }
];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getBorrowerName(recon: BankerRecon): string {
  const snapshot = recon.loan_snapshot as Record<string, unknown> | null;
  if (!snapshot) {
return '';
}
  return [snapshot.borrower_last_name, snapshot.borrower_first_name]
    .filter(Boolean)
    .join(', ');
}

// LO = the recon payee (lo_user_id), never the Encompass loan officer name.
function getLoName(recon: BankerRecon): string {
  return [recon.lo?.first_name, recon.lo?.last_name]
    .filter(Boolean)
    .join(' ');
}

function getEpoStatusLabel(status: BankerRecon['early_payoff_calculation_status']): string {
  const labels: Record<NonNullable<BankerRecon['early_payoff_calculation_status']>, string> = {
    calculated: 'Calculated',
    missing_investor: 'Missing investor',
    missing_rule: 'Missing investor EPO rule',
    missing_purchase_date: 'Missing investor purchase date',
    missing_first_payment_date: 'Missing first investor payment date',
    invalid_rule: 'Invalid investor EPO rule',
    unsupported_rule: 'Unsupported investor EPO rule'
  };
  return status ? labels[status] : '-';
}

function getTimePeriodBounds(period: TimePeriod): { start: Date; end: Date } | null {
  if (period === 'all') {
return null;
}

  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth(); // 0-indexed
  const quarter = Math.floor(month / 3);

  switch (period) {
    case 'this_month':
      return {
        start: new Date(year, month, 1),
        end: new Date(year, month + 1, 0, 23, 59, 59, 999)
      };
    case 'last_month': {
      const lm = month === 0 ? 11 : month - 1;
      const ly = month === 0 ? year - 1 : year;
      return {
        start: new Date(ly, lm, 1),
        end: new Date(ly, lm + 1, 0, 23, 59, 59, 999)
      };
    }
    case 'this_quarter':
      return {
        start: new Date(year, quarter * 3, 1),
        end: new Date(year, quarter * 3 + 3, 0, 23, 59, 59, 999)
      };
    case 'last_quarter': {
      const lq = quarter === 0 ? 3 : quarter - 1;
      const lqy = quarter === 0 ? year - 1 : year;
      return {
        start: new Date(lqy, lq * 3, 1),
        end: new Date(lqy, lq * 3 + 3, 0, 23, 59, 59, 999)
      };
    }
    case 'this_year':
      return {
        start: new Date(year, 0, 1),
        end: new Date(year, 11, 31, 23, 59, 59, 999)
      };
    case 'last_year':
      return {
        start: new Date(year - 1, 0, 1),
        end: new Date(year - 1, 11, 31, 23, 59, 59, 999)
      };
    default:
      return null;
  }
}

function getSortValue(recon: BankerRecon, field: SortField): string | number | null {
  switch (field) {
    case 'loan_number':
      return (recon.loan_number || '').toLowerCase();
    case 'borrower':
      return getBorrowerName(recon).toLowerCase();
    case 'lo':
      return getLoName(recon).toLowerCase();
    case 'loan_amount':
      return Number(recon.loan_amount) || 0;
    case 'net_profit':
      return recon.net_profit !== null && recon.net_profit !== undefined ? Number(recon.net_profit) : null;
    case 'lo_payout':
      return recon.lo_payout !== null && recon.lo_payout !== undefined ? Number(recon.lo_payout) : null;
    case 'investor_purchase_date':
      return recon.investor_purchase_date
        ? parseAsLocalDate(recon.investor_purchase_date).getTime()
        : null;
    case 'early_payoff_date':
      return recon.early_payoff_date
        ? parseAsLocalDate(recon.early_payoff_date).getTime()
        : null;
    case 'finalized_at':
      return recon.finalized_at
        ? parseAsLocalDate(recon.finalized_at).getTime()
        : null;
    default:
      return null;
  }
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function FinalizedReconsTable({ recons, loading, onViewRecon }: FinalizedReconsTableProps) {
  const [search, setSearch] = useState('');
  const [sortField, setSortField] = useState<SortField | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>(null);
  const [timePeriod, setTimePeriod] = useState<TimePeriod>('all');

  // 3-click sort cycle: asc -> desc -> clear
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      if (sortDirection === 'asc') {
        setSortDirection('desc');
      } else if (sortDirection === 'desc') {
        setSortField(null);
        setSortDirection(null);
      }
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const filteredRecons = useMemo(() => {
    let result = recons;

    // Search filter
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      result = result.filter((r) => {
        const loanMatch = r.loan_number.toLowerCase().includes(q);
        const borrowerMatch = getBorrowerName(r).toLowerCase().includes(q);
        const loMatch = getLoName(r).toLowerCase().includes(q);
        return loanMatch || borrowerMatch || loMatch;
      });
    }

    // Time period filter
    const bounds = getTimePeriodBounds(timePeriod);
    if (bounds) {
      result = result.filter((r) => {
        if (!r.finalized_at) {
return false;
}
        const d = parseAsLocalDate(r.finalized_at);
        return d >= bounds.start && d <= bounds.end;
      });
    }

    // Sort
    if (sortField && sortDirection) {
      result = [...result].sort((a, b) => {
        const aVal = getSortValue(a, sortField);
        const bVal = getSortValue(b, sortField);

        if (aVal === bVal) {
return 0;
}
        if (aVal === null) {
return 1;
}
        if (bVal === null) {
return -1;
}

        const comparison = aVal < bVal ? -1 : 1;
        return sortDirection === 'asc' ? comparison : -comparison;
      });
    }

    return result;
  }, [recons, search, timePeriod, sortField, sortDirection]);

  const hasActiveFilters = search.trim() !== '' || timePeriod !== 'all';
  const showingSubset = filteredRecons.length !== recons.length;

  if (loading) {
    return (
      <Card>
        <CardContent className="p-0">
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            <span className="ml-2 text-sm text-muted-foreground">Loading finalized records...</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent className="p-0">
        {/* Search + Filter Bar */}
        <div className="flex flex-wrap items-center gap-3 px-4 py-3 border-b">
          {/* Search */}
          <div className="relative flex-1 min-w-[200px] max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search loan #, borrower, LO..."
              aria-label="Search loan #, borrower, LO"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 bg-background"
            />
            {search && (
              <button
                type="button"
                aria-label="Clear finalized records search"
                onClick={() => setSearch('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>

          {/* Time Period */}
          <Select value={timePeriod} onValueChange={(v) => setTimePeriod(v as TimePeriod)}>
            <SelectTrigger className="w-[160px]" aria-label="Filter finalized records by time period">
              <SelectValue placeholder="Time Period" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Time</SelectItem>
              <SelectItem value="this_month">This Month</SelectItem>
              <SelectItem value="last_month">Last Month</SelectItem>
              <SelectItem value="this_quarter">This Quarter</SelectItem>
              <SelectItem value="last_quarter">Last Quarter</SelectItem>
              <SelectItem value="this_year">This Year</SelectItem>
              <SelectItem value="last_year">Last Year</SelectItem>
            </SelectContent>
          </Select>

          {/* Record count */}
          {showingSubset && (
            <span className="text-xs text-muted-foreground">
              Showing {filteredRecons.length} of {recons.length}
            </span>
          )}
        </div>

        {/* Table */}
        {recons.length === 0 && !hasActiveFilters ? (
          <div className="text-center py-12 text-muted-foreground">
            No finalized reconciliations found.
          </div>
        ) : filteredRecons.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            No matching records found. Try adjusting your search or filters.
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                {COLUMNS.map((col) => (
                  <TableHead
                    key={col.key}
                    scope="col"
                    aria-sort={sortField === col.key && sortDirection
                      ? sortDirection === 'asc' ? 'ascending' : 'descending'
                      : undefined}
                    className={cn(
                      col.align === 'right' && 'text-right',
                      col.sortable && 'p-0 hover:bg-muted/70 select-none transition-colors',
                      sortField === col.key && 'bg-primary/10 text-primary'
                    )}
                  >
                    {col.sortable ? (
                      <button
                        type="button"
                        onClick={() => handleSort(col.key)}
                        className={cn(
                          'flex h-10 w-full items-center gap-1 whitespace-nowrap px-2 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring',
                          col.align === 'right' && 'justify-end'
                        )}
                      >
                        {col.label}
                        {sortField === col.key ? (
                          sortDirection === 'asc' ? (
                            <ChevronUp aria-hidden="true" className="w-3 h-3" />
                          ) : (
                            <ChevronDown aria-hidden="true" className="w-3 h-3" />
                          )
                        ) : (
                          <ChevronUp aria-hidden="true" className="w-3 h-3 opacity-0" />
                        )}
                      </button>
                    ) : col.label}
                  </TableHead>
                ))}
                {/* Actions column */}
                <TableHead scope="col" className="text-center w-16">
                  <span className="sr-only">Actions</span>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredRecons.map((recon) => {
                const borrower = getBorrowerName(recon) || '-';
                const loName = getLoName(recon) || '-';
                return (
                  <TableRow key={recon.id}>
                    <TableCell className="font-medium">{recon.loan_number}</TableCell>
                    <TableCell>{borrower}</TableCell>
                    <TableCell>{loName}</TableCell>
                    <TableCell className="text-right">
                      ${recon.loan_amount.toLocaleString()}
                    </TableCell>
                    <TableCell className="text-right">
                      {recon.net_profit !== null && recon.net_profit !== undefined
                        ? `$${recon.net_profit.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                        : '-'}
                    </TableCell>
                    <TableCell className="text-right">
                      {recon.lo_payout !== null && recon.lo_payout !== undefined
                        ? `$${recon.lo_payout.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                        : '-'}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {recon.investor_purchase_date
                        ? parseAsLocalDate(recon.investor_purchase_date).toLocaleDateString()
                        : '-'}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {recon.early_payoff_date
                        ? parseAsLocalDate(recon.early_payoff_date).toLocaleDateString()
                        : recon.early_payoff_calculation_status
                          ? getEpoStatusLabel(recon.early_payoff_calculation_status)
                          : '-'}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {recon.finalized_at
                        ? parseAsLocalDate(recon.finalized_at).toLocaleDateString()
                        : '-'}
                    </TableCell>
                    <TableCell className="text-center">
                      <Button
                        aria-label={`View reconciliation for loan ${recon.loan_number}`}
                        variant="ghost"
                        size="sm"
                        className="h-8 w-8 p-0"
                        onClick={() => onViewRecon(recon)}
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}
